/**
 * imports
 */
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import './scss/product/main.scss'

import axios from 'axios'
import VueAxios from 'vue-axios'

import VueScrollTo from "vue-scrollto"
import Flickity from 'vue-flickity'
import VueLazyload from 'vue-lazyload'
import VideoPlay from './vue/components/video-play'
import vSelect from "vue-select"
import MobileFooterNavigation from "./vue/components/mobile-footer-navigation"

const pinia = createPinia()

const createVueApp = () => {
  const app = createApp({})

  app.component('VueFlickity', Flickity)
  app.component('VideoPlay', VideoPlay)
  app.component("VSelect", vSelect)
  app.use(VueAxios, axios)
  app.use(VueScrollTo)
  app.use(VueLazyload)
  app.component("MobileFooterNavigation", MobileFooterNavigation)

  /**
   * vue components
   * auto-import all vue components
   */
  const vueComponents = require.context('./vue/components-bundle/components-product/', true, /\.(vue|js)$/)

  vueComponents.keys().forEach(key => {
    const component = vueComponents(key).default

    const name = component.name
      ? component.name
      : key.replace(/\.(\/|vue|js)/g, '').replace(/(\/|-|_|\s)\w/g, (match) => match.slice(1).toUpperCase())

    app.component(name, component)
  })

  app.use(pinia)

  return app
}

const captureSubscriptionPerksSsr = (root = document) => {
  const captures = []
  root.querySelectorAll('product-selling-plans').forEach((host) => {
    const perks = host.querySelector('product-subscription-perks')
    if (!perks) {
      captures.push(null)
      return
    }
    captures.push({
      html: perks.innerHTML,
      ssrVisible: perks.getAttribute('data-ssr-visible') || 'false',
      hideOnOneTime: perks.getAttribute('data-hide-on-one-time') || 'false'
    })
  })
  window.__subscriptionPerksCaptures = captures
}

function bootPrimaryMounts () {
  const appElement = document.querySelector('#MainContent')
  if (appElement) {
    captureSubscriptionPerksSsr()
    const app = createVueApp()
    app.mount(appElement)
    return
  }

  const vueElements = document.querySelectorAll('[vue]')
  vueElements.forEach((el) => {
    captureSubscriptionPerksSsr()
    const app = createVueApp()
    app.mount(el)
  })
}

bootPrimaryMounts()

/**
 * fixes for Shopify sections
 * 1. properly render vue components on user insert in the theme editor
 * 2. reload the current page to rerender async inserted sections with vue components
 *
 * add the 'vue' keyword to the section's wrapper classes if the section uses any vue functionality e.g.:
 * {% schema %}
 * {
 *   "class": "vue-section"
 * }
 * {% endschema %}
 */
if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.classList.value.includes('vue')) {
      captureSubscriptionPerksSsr(event.target)
      createVueApp().mount(event.target)
    }
  })
} else if (!Shopify.designMode && process.env.NODE_ENV === 'development') {
  new MutationObserver((mutationsList) => {
    mutationsList.forEach(record => {
      const vue = Array.from(record.addedNodes).find(node => node.classList && node.classList.value.includes('vue'))
      if (vue) window.location.reload()
    })
  }).observe(document.body, {
    childList: true,
    subtree: true
  })
}

function scrollToReviews() {
  const hasHash = window.location.hash === '#reviews'
  if (!hasHash) return

  const scrollToWidget = () => {
    const reviewsEl = document.querySelector('#judgeme_product_reviews') || document.querySelector('.jdgm-rev-widg')
    if (!reviewsEl) return false

    reviewsEl.scrollIntoView({ behavior: 'smooth', block: 'start' })

    return true
  }

  if (scrollToWidget()) return

  if (!document.body) return

  const observer = new MutationObserver((mutations, obs) => {
    if (scrollToWidget()) {
      obs.disconnect()
    }
  })

  observer.observe(document.body, { childList: true, subtree: true })

  setTimeout(() => {
    observer.disconnect()
  }, 5000)
}

if (document.readyState === 'loading') {
  window.addEventListener('DOMContentLoaded', scrollToReviews)
} else {
  scrollToReviews()
}
